import type { AxiosInstance } from 'axios';
import { z } from 'zod';
import { getLeads } from '@/api/generated/endpoints/leads/leads.ts';
import {
  CreateNoteBody,
  CreateNoteParams,
  FindNotesParams,
} from '@/api/generated/endpoints/leads/leads.zod';
import {
  FindLeadsQueryParams,
  FindLeadsResponse,
  PatchLeadBody,
  PatchLeadParams,
  PatchWorkflowParams,
} from '@/api/generated/endpoints/leads/leads.zod.ts';
import type { FindLeadsParams } from '@/api/generated/models/findLeadsParams.ts';
import type { ServiceSuccessResponse } from '@/models/common/serviceResponse';
import { fromAxios } from '@/models/common/serviceResponse';
import {
  type LeadId,
  type LeadNote,
  LeadNoteSchema,
  LeadSchema,
  Leads,
  type Mileage,
  type Note,
  type SelectedDealerIds,
  WorkflowPatch,
} from '@/models/entities/lead';

export interface ILeadService {
  getLeads(
    dealerIds: SelectedDealerIds,
  ): Promise<ServiceSuccessResponse<Leads>>;

  getNotes(leadId: LeadId): Promise<ServiceSuccessResponse<LeadNote[]>>;

  updateNote(
    leadId: LeadId,
    note: Note | null,
  ): Promise<ServiceSuccessResponse<null>>;

  updateOdometer(
    leadId: LeadId,
    odometer: Mileage | null,
  ): Promise<ServiceSuccessResponse<null>>;

  patchWorkflow(
    leadId: LeadId,
    patch: WorkflowPatch,
  ): Promise<ServiceSuccessResponse<null>>;
}

class LeadService implements ILeadService {
  private readonly MaxQueryDealerIds = 200;

  private readonly apiClient;

  constructor(axios: AxiosInstance) {
    this.apiClient = getLeads(axios);
  }

  async getLeads(
    dealerIds: SelectedDealerIds,
  ): Promise<ServiceSuccessResponse<Leads>> {
    const query =
      dealerIds.size > this.MaxQueryDealerIds
        ? {}
        : { dealerId: Array.from(dealerIds) };

    const params =
      dealerIds.size > this.MaxQueryDealerIds
        ? ({} as unknown as FindLeadsParams)
        : FindLeadsQueryParams.parse(query);

    const response = await this.apiClient.findLeads(params);

    const validated = FindLeadsResponse.parse(response.data);
    const domain = validated.map((l) => LeadSchema.parse(l));
    return fromAxios(Leads.from(domain), response);
  }
  async getNotes(leadId: LeadId): Promise<ServiceSuccessResponse<LeadNote[]>> {
    const params = FindNotesParams.parse({ leadId });
    const response = await this.apiClient.findNotes(params.leadId);
    const domainNotes = z.array(LeadNoteSchema).parse(response.data);
    return fromAxios(domainNotes, response);
  }

  async updateNote(
    leadId: LeadId,
    note: Note | null,
  ): Promise<ServiceSuccessResponse<null>> {
    if (note === null || note.trim() === '') {
      return { domain: null } as unknown as ServiceSuccessResponse<null>;
    }
    const params = CreateNoteParams.parse({ leadId });
    const encodedBody = CreateNoteBody.parse({ content: note });

    const response = await this.apiClient.createNote(
      params.leadId,
      encodedBody,
    );
    return fromAxios(null, response);
  }

  async updateOdometer(
    leadId: LeadId,
    odometer: Mileage | null,
  ): Promise<ServiceSuccessResponse<null>> {
    const params = PatchLeadParams.parse({ leadId });
    const encodedPatch = PatchLeadBody.parse({ odometer });

    const response = await this.apiClient.patchLead(
      params.leadId,
      encodedPatch,
    );
    return fromAxios(null, response);
  }

  async patchWorkflow(
    leadId: LeadId,
    patch: WorkflowPatch,
  ): Promise<ServiceSuccessResponse<null>> {
    const params = PatchWorkflowParams.parse({ leadId });
    const encodedPatch = WorkflowPatch.encode(patch);
    const response = await this.apiClient.patchWorkflow(
      params.leadId,
      encodedPatch,
    );
    return fromAxios(null, response);
  }
}

class LeadServiceSingleton implements ILeadService {
  private static instance: ILeadService | null = null;

  public static init(axios: AxiosInstance): void {
    LeadServiceSingleton.instance = new LeadService(axios);
  }

  public static mock(mock: ILeadService): void {
    LeadServiceSingleton.instance = mock;
  }

  private static getInstance(): ILeadService {
    if (LeadServiceSingleton.instance == null) {
      throw new Error('LeadService not initialized');
    } else {
      return LeadServiceSingleton.instance;
    }
  }

  getLeads(
    dealerIds: SelectedDealerIds,
  ): Promise<ServiceSuccessResponse<Leads>> {
    return LeadServiceSingleton.getInstance().getLeads(dealerIds);
  }

  getNotes(leadId: LeadId): Promise<ServiceSuccessResponse<LeadNote[]>> {
    return LeadServiceSingleton.getInstance().getNotes(leadId);
  }

  patchWorkflow(
    leadId: LeadId,
    patch: WorkflowPatch,
  ): Promise<ServiceSuccessResponse<null>> {
    return LeadServiceSingleton.getInstance().patchWorkflow(leadId, patch);
  }

  updateNote(
    leadId: LeadId,
    note: Note | null,
  ): Promise<ServiceSuccessResponse<null>> {
    return LeadServiceSingleton.getInstance().updateNote(leadId, note);
  }

  updateOdometer(
    leadId: LeadId,
    odometer: Mileage | null,
  ): Promise<ServiceSuccessResponse<null>> {
    return LeadServiceSingleton.getInstance().updateOdometer(leadId, odometer);
  }
}

export const InitLeadService = (axios: AxiosInstance) =>
  LeadServiceSingleton.init(axios);

export const MockLeadService = (mock: ILeadService) =>
  LeadServiceSingleton.mock(mock);

export const leadService: ILeadService = new LeadServiceSingleton();
